import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, hotAnime, latestUpdates } from '@/mocks/anime';

const categoryContent: Record<string, { title: string; description: string; keywords: string }> = {
  riman: { title: '日漫', description: '日本动漫精选合集，涵盖热血、治愈、奇幻、科幻等全类型日漫作品，高清画质下拉式阅读，每日同步更新。', keywords: '日漫,日本动漫,热血动漫,治愈动漫,奇幻动漫,免费日漫' },
  hanman: { title: '韩漫', description: '韩国漫画精选合集，涵盖恋爱、悬疑、剧情、奇幻等全类型韩漫作品，高清画质下拉式阅读，每日同步更新。', keywords: '韩漫,韩国漫画,恋爱漫画,悬疑漫画,奇幻漫画,免费韩漫' },
  guoman: { title: '国漫', description: '国产动漫精选合集，涵盖仙侠、玄幻、都市、科幻等全类型国漫作品，高清画质下拉式阅读，每日同步更新。', keywords: '国漫,国产动漫,仙侠动漫,玄幻动漫,都市动漫,免费国漫' },
  shaonian: { title: '少年漫', description: '少年热血动漫精选，战斗、冒险、友情主题，燃爆你的青春热血。', keywords: '少年漫,热血动漫,战斗动漫,冒险动漫,友情动漫' },
  shaonv: { title: '少女漫', description: '少女恋爱动漫精选，甜蜜恋爱、唯美画风，温暖你的少女心。', keywords: '少女漫,恋爱动漫,甜蜜动漫,唯美动漫,校园动漫' },
  qihuan: { title: '奇幻', description: '奇幻异世界动漫精选，魔法、冒险、异世界转生，开启想象之旅。', keywords: '奇幻动漫,异世界动漫,魔法动漫,冒险动漫,转生动漫' },
};

export default function Category() {
  const { slug = 'riman' } = useParams<{ slug: string }>();
  const info = categoryContent[slug] || categoryContent.riman;
  const [activeSlug, setActiveSlug] = useState(slug);
  const [filteredAnime, setFilteredAnime] = useState(hotAnime);
  const [sortBy, setSortBy] = useState<'latest' | 'rating'>('latest');

  useEffect(() => {
    setActiveSlug(slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  useEffect(() => {
    const categoryName = categories.find((c) => c.slug === activeSlug)?.name || '';
    let items = [...hotAnime, ...hotAnime.map((a) => ({ ...a, id: a.id + 100, slug: a.slug + '-v2' }))];
    if (categoryName) {
      items = items.filter((a) => a.category === categoryName);
    }
    if (items.length === 0) items = [...hotAnime];
    if (sortBy === 'rating') {
      items.sort((a, b) => b.rating - a.rating);
    }
    setFilteredAnime(items.slice(0, 12));
  }, [activeSlug, sortBy]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        url: `https://www.51dongman.com/category/${activeSlug}`,
        name: `${info.title} - 51动漫分类`,
        description: info.description,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51dongman.com' },
          { '@type': 'ListItem', position: 2, name: info.title },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <main>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: info.title }]} />

          <section className="py-8 md:py-12 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-3">{info.title}</h1>
              <p className="text-foreground-600 text-sm md:text-base mb-8">{info.description}</p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeSlug === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === 'latest' ? 'bg-secondary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                    }`}
                  >
                    最新
                  </button>
                  <button
                    onClick={() => setSortBy('rating')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === 'rating' ? 'bg-secondary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                    }`}
                  >
                    评分
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredAnime.map((anime) => (
                  <Link
                    key={anime.id}
                    to={`/article/${anime.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={anime.imageUrl}
                        alt={anime.title}
                        title={`${anime.title} - 51动漫${info.title}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-background-950/70 text-accent-400 text-xs font-semibold">
                        {anime.rating}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                        <span className="text-background-50 text-xs">{anime.updateTo}</span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-medium text-foreground-950 text-sm md:text-base group-hover:text-primary-600 transition-colors line-clamp-1">
                        {anime.title}
                      </h3>
                      <p className="text-foreground-500 text-xs mt-1">{anime.category}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </main>
    </>
  );
}